import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
} from '@mui/material';
import {
  People as PeopleIcon,
  Policy as PolicyIcon,
  TrendingUp,
  Warning,
  CheckCircle,
  Schedule,
  AttachMoney,
  Description,
} from '@mui/icons-material';
import { useAuth } from 'hooks/useAuth';
import { dashboardAPI } from 'services/api';
import { useNavigate } from 'react-router-dom';

const AgentDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);

  const loadDashboardData = useCallback(async () => {
    if (!user) {
      console.warn('User not available, skipping dashboard data load');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Load agent dashboard data
      const dashboardResponse = await dashboardAPI.getAgentDashboard(user.id);
      setDashboardData(dashboardResponse.data);
      
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      loadDashboardData();
    }
  }, [user, loadDashboardData]);

  const StatCard = ({ title, value, icon, color, onClick, loading: cardLoading = false }) => (
    <Card 
      sx={{ 
        height: '100%', 
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick ? { boxShadow: 3 } : {}
      }}
      onClick={onClick}
    >
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="textSecondary" gutterBottom variant="h6">
              {title}
            </Typography>
            <Typography variant="h4" component="h2" color={color || 'primary'}>
              {cardLoading ? <CircularProgress size={24} /> : value}
            </Typography>
          </Box>
          <Box color={color || 'primary.main'}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={loadDashboardData}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="subtitle1" color="textSecondary" gutterBottom>
        Welcome back, {user?.name}! Here's your overview of the insurance system.
      </Typography>

      <Grid container spacing={3} sx={{ mt: 2 }}>
        {/* Statistics Cards */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Farmers"
            value={dashboardData?.total_farmers || 0}
            icon={<PeopleIcon sx={{ fontSize: 40 }} />}
            color="primary.main"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Policies"
            value={dashboardData?.active_policies || 0}
            icon={<PolicyIcon sx={{ fontSize: 40 }} />}
            color="success.main"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Pending Claims"
            value={dashboardData?.pending_claims || 0}
            icon={<Warning sx={{ fontSize: 40 }} />}
            color="warning.main"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Approved Claims"
            value={dashboardData?.approved_claims || 0}
            icon={<CheckCircle sx={{ fontSize: 40 }} />}
            color="success.main"
          />
        </Grid>

        {/* Financial Overview */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Financial Overview
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <AttachMoney color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Total Premium Collected"
                    secondary={`₹${(dashboardData?.total_premium_collected || 0).toFixed(2)}`}
                  />
                </ListItem>
                <Divider />
                <ListItem>
                  <ListItemIcon>
                    <TrendingUp color="info" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Total Claims Paid"
                    secondary={`₹${(dashboardData?.total_claims_paid || 0).toLocaleString()}`}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Pending Items */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Pending Items
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <Description color="warning" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Document Verifications"
                    secondary={`${dashboardData?.pending_document_verifications || 0} documents pending`}
                  />
                </ListItem>
                <Divider />
                <ListItem>
                  <ListItemIcon>
                    <Schedule color="info" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Claims Processing"
                    secondary={`${dashboardData?.pending_claims || 0} claims pending review`}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <Box display="flex" gap={2} flexWrap="wrap">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => navigate('/admin/documents')}
                >
                  Verify Documents
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => navigate('/admin/requests')}
                >
                  Review Requests
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => navigate('/admin/analytics')}
                >
                  View Analytics
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AgentDashboard;
